/**
 * Chunking helpers for api-server batch endpoints that cap ids per request
 * (`fetchOpsTodosBatch`, `fetchAppointmentFilesBatch`).
 */

export function chunkIds(ids: string[], size: number): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}

/**
 * `Promise.allSettled` with a concurrency cap: runs at most `limit` tasks at a
 * time and resolves to one settled result per task, in task order. Identical
 * semantics to `allSettled` (never rejects; each task settles independently) —
 * only the in-flight count differs.
 */
export async function settleWithLimit<T>(
  tasks: Array<() => Promise<T>>,
  limit: number,
): Promise<PromiseSettledResult<T>[]> {
  const results: PromiseSettledResult<T>[] = new Array(tasks.length);
  let nextIndex = 0;

  const runWorker = async (): Promise<void> => {
    while (nextIndex < tasks.length) {
      const index = nextIndex;
      nextIndex += 1;
      try {
        results[index] = { status: "fulfilled", value: await tasks[index]!() };
      } catch (reason) {
        results[index] = { status: "rejected", reason };
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(limit, tasks.length) }, runWorker),
  );
  return results;
}
